import {
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography
} from '@mui/material'
import { startCase } from 'lodash'
import React from 'react'
import { Link } from 'react-router-dom'

import GlyphIcon from 'components/common/GlyphIcon'
import Icon from 'components/Icon'
import { HaltButton } from 'components/navigation/Notifications/NotificationsList/HaltButton'
import {
  ICON_BACKGROUND_COLOR_RUNNING_TEST,
  NOTIFICATION_LEFT_PADDING
} from 'components/navigation/Notifications/utils'
import {
  getCurrentStepForRun,
  getRunStage,
  getScenarioStepCount,
  isRunHalting,
  isRunInCompletedStage
} from 'components/scenarios/utils'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { ScenarioRun } from 'types/api/scenarios/runs/scenario-runs'
import { Scenario } from 'types/api/scenarios/scenario'

const StatusText: React.FC<{
  runStage?: string
  isHalting?: boolean
  isCompleted?: boolean
  currentStep?: string
  totalSteps?: number
}> = ({ runStage, isHalting, isCompleted, currentStep, totalSteps }) => {
  if (isHalting) {
    return <span>Halting...</span>
  }

  if (isCompleted) {
    return <span>{runStage ? startCase(runStage) : ''}</span>
  }

  return (
    <Typography variant="caption">
      Scenario:{' '}
      <span data-test-status>
        Step {currentStep} of {totalSteps} is running...
      </span>
    </Typography>
  )
}

export const ActiveScenarioListItem: React.FC<{
  scenario: Scenario
  latestRun: ScenarioRun
  doFetch(): void
  onConfirmHalt(options: Record<string, any>): void
}> = ({ scenario, latestRun, doFetch, onConfirmHalt, ...rest }) => {
  const { currentTeamId } = useAuth()
  const scenarioId = scenario.guid

  const totalSteps = getScenarioStepCount(scenario)
  const currentStep = getCurrentStepForRun(latestRun)
  const runNumber = latestRun?.run_number
  const isCompleted = isRunInCompletedStage(latestRun)
  const isHalting = isRunHalting(latestRun)

  const runStage = getRunStage(scenario)

  const scenarioUrl = runNumber
    ? `/scenarios/detail/${scenarioId}/runs/${runNumber}`
    : `/scenarios/detail/${scenarioId}`

  const handleConfirmRequest = ({ reason } = { reason: '' }) =>
    onConfirmHalt({
      attack: scenario,
      type: 'scenario',
      currentTeamId,
      reason,
      runNumber
    })

  return (
    <ListItem
      data-test-notification-item
      disablePadding
      component="div"
      secondaryAction={
        <HaltButton
          fetchAttack={doFetch}
          isHalting={isHalting}
          runInfo={latestRun}
          title="Halt scenario"
          trackedEvent="SingleScenarioHalted"
          trackingOptions={{ scenarioId, runNumber: runNumber }}
          type="scenario"
          onConfirmRequest={handleConfirmRequest}
        />
      }
      {...rest}
    >
      <ListItemButton component={Link} to={scenarioUrl}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ pl: NOTIFICATION_LEFT_PADDING, width: '100%' }}
        >
          <GlyphIcon
            css={{
              marginRight: theme.space[3]
            }}
          >
            <Icon
              css={{
                fontSize: theme.fontSizes[4],
                color: ICON_BACKGROUND_COLOR_RUNNING_TEST
              }}
              icon="clipboardList"
            />
          </GlyphIcon>
          <ListItemText
            data-test-notification-text
            primary={scenario.name}
            secondary={
              <StatusText
                currentStep={currentStep}
                isCompleted={isCompleted}
                isHalting={isHalting}
                runStage={runStage}
                totalSteps={totalSteps}
              />
            }
          />
        </Stack>
      </ListItemButton>
    </ListItem>
  )
}
